/**
 * Cache manager module
 *
 * This module:
 * - Manages cache operations
 * - Handles cache invalidation
 * - Provides utilities for working with the cache
 * - Handles all error states with typed errors
 */
import {
  CacheManifest,
  AssetMetadata,
  AssetType,
  ServiceWorkerMessageType,
} from "../config/types";
import {
  DEFAULT_CACHE_NAME,
  DEFAULT_CACHE_TTL,
  CACHE_MANIFEST_PATH,
} from "../config/constants";
import {
  debugLog,
  determineAssetType,
  formatSize,
} from "../utils/cache-helpers";
import { generateCacheKey } from "../utils/hash-generator";
import { sendMessageToSW, isServiceWorkerActive } from "./register-sw";
import {
  CacheError,
  CacheErrorCode,
  CacheAPINotSupportedError,
  CacheNotInitializedError,
  CacheOpenFailedError,
  ManifestParseError,
  ManifestSaveFailedError,
  StorageQuotaExceededError,
  StorageAccessDeniedError,
  isQuotaExceededError,
  isStorageAccessDeniedError,
  wrapError,
} from "./errors";

/**
 * Initialization state of the cache manager
 */
export enum CacheManagerState {
  UNINITIALIZED = "UNINITIALIZED",
  INITIALIZING = "INITIALIZING",
  READY = "READY",
  ERROR = "ERROR",
  UNSUPPORTED = "UNSUPPORTED",
}

/**
 * Result of initialization attempt
 */
export interface InitResult {
  success: boolean;
  state: CacheManagerState;
  error?: CacheError;
}

/**
 * Cache manager class with proper error handling
 */
export class CacheManager {
  private static cache: Cache | null = null;
  private static manifest: CacheManifest | null = null;
  private static state: CacheManagerState = CacheManagerState.UNINITIALIZED;
  private static lastError: CacheError | null = null;
  private static initPromise: Promise<InitResult> | null = null;

  /**
   * Get the current state of the cache manager
   */
  static getState(): CacheManagerState {
    return this.state;
  }

  /**
   * Get the last error that occurred
   */
  static getLastError(): CacheError | null {
    return this.lastError;
  }

  /**
   * Check if the cache manager is ready for operations
   */
  static isReady(): boolean {
    return this.state === CacheManagerState.READY;
  }

  /**
   * Check if caching is supported in the current environment
   */
  static isSupported(): boolean {
    return typeof window !== "undefined" && "caches" in window;
  }

  /**
   * Initialize the cache manager
   * @param cacheName Name of the cache to use
   * @returns Promise resolving to initialization result
   */
  static async init(cacheName: string = DEFAULT_CACHE_NAME): Promise<InitResult> {
    // Return existing promise if initialization is in progress
    if (this.initPromise) {
      return this.initPromise;
    }

    // Already initialized successfully
    if (this.state === CacheManagerState.READY) {
      return { success: true, state: this.state };
    }

    // Already determined to be unsupported
    if (this.state === CacheManagerState.UNSUPPORTED) {
      const error = new CacheAPINotSupportedError();
      return { success: false, state: this.state, error };
    }

    this.initPromise = this.performInit(cacheName);
    const result = await this.initPromise;
    this.initPromise = null;
    return result;
  }

  /**
   * Perform the actual initialization
   */
  private static async performInit(cacheName: string): Promise<InitResult> {
    this.state = CacheManagerState.INITIALIZING;
    this.lastError = null;

    // Check for Cache API support
    if (!this.isSupported()) {
      const error = new CacheAPINotSupportedError();
      this.lastError = error;
      this.state = CacheManagerState.UNSUPPORTED;
      debugLog("Cache API not supported in this browser");
      return { success: false, state: this.state, error };
    }

    try {
      // Open the cache
      this.cache = await caches.open(cacheName);
      debugLog("Cache opened:", cacheName);

      // Load the manifest
      await this.loadManifest();

      this.state = CacheManagerState.READY;
      debugLog("Cache manager initialized successfully");
      return { success: true, state: this.state };
    } catch (error) {
      const cacheError = this.handleInitError(error, cacheName);
      this.lastError = cacheError;
      this.state = CacheManagerState.ERROR;
      debugLog("Failed to initialize cache manager:", cacheError);
      return { success: false, state: this.state, error: cacheError };
    }
  }

  /**
   * Handle initialization errors and convert to typed errors
   */
  private static handleInitError(error: unknown, cacheName: string): CacheError {
    if (isStorageAccessDeniedError(error)) {
      return new StorageAccessDeniedError(error as Error);
    }
    if (isQuotaExceededError(error)) {
      return new StorageQuotaExceededError(error as Error);
    }
    return new CacheOpenFailedError(
      cacheName,
      error instanceof Error ? error : undefined
    );
  }

  /**
   * Reset the cache manager (useful for recovery or testing)
   */
  static reset(): void {
    this.cache = null;
    this.manifest = null;
    this.state = CacheManagerState.UNINITIALIZED;
    this.lastError = null;
    this.initPromise = null;
    debugLog("Cache manager reset");
  }

  /**
   * Attempt to recover from an error state
   * @param cacheName Name of the cache to use
   */
  static async recover(cacheName: string = DEFAULT_CACHE_NAME): Promise<InitResult> {
    if (this.state === CacheManagerState.UNSUPPORTED) {
      // Cannot recover from unsupported state
      return {
        success: false,
        state: this.state,
        error: new CacheAPINotSupportedError(),
      };
    }

    this.reset();
    return this.init(cacheName);
  }

  /**
   * Load the cache manifest
   */
  static async loadManifest(): Promise<void> {
    try {
      if (!this.cache) {
        const result = await this.init();
        if (!result.success) {
          throw result.error || new CacheNotInitializedError("loadManifest");
        }
      }

      if (!this.cache) {
        throw new CacheNotInitializedError("loadManifest");
      }

      // Try to get the manifest from the cache
      const manifestResponse = await this.cache.match(CACHE_MANIFEST_PATH);

      if (manifestResponse) {
        try {
          this.manifest = await manifestResponse.json();
          debugLog("Cache manifest loaded:", this.manifest);
        } catch (parseError) {
          // Handle JSON parse errors
          debugLog("Failed to parse manifest, creating new one:", parseError);
          this.manifest = this.createNewManifest();
        }
      } else {
        // Create a new manifest
        this.manifest = this.createNewManifest();
        debugLog("New cache manifest created");
      }
    } catch (error) {
      debugLog("Failed to load cache manifest:", error);

      // Create a new manifest as fallback
      this.manifest = this.createNewManifest();

      // Re-throw if it's a critical error
      if (error instanceof CacheError && !error.recoverable) {
        throw error;
      }
    }
  }

  /**
   * Create a new empty manifest
   */
  private static createNewManifest(): CacheManifest {
    return {
      version: "1",
      generatedAt: new Date().toISOString(),
      assets: {},
    };
  }

  /**
   * Ensure the cache manager is initialized
   */
  private static async ensureInitialized(): Promise<void> {
    if (this.state === CacheManagerState.UNSUPPORTED) {
      throw new CacheAPINotSupportedError();
    }

    if (this.state !== CacheManagerState.READY) {
      const result = await this.init();
      if (!result.success) {
        throw result.error || new CacheNotInitializedError();
      }
    }
  }

  /**
   * Save the cache manifest
   */
  static async saveManifest(): Promise<void> {
    try {
      await this.ensureInitialized();

      if (!this.cache || !this.manifest) {
        throw new CacheNotInitializedError("saveManifest");
      }

      // Create a response with the manifest
      const manifestResponse = new Response(JSON.stringify(this.manifest), {
        headers: { "Content-Type": "application/json" },
      });

      // Store in the cache
      await this.cache.put(CACHE_MANIFEST_PATH, manifestResponse);

      // If service worker is active, update it too
      if (isServiceWorkerActive()) {
        try {
          await sendMessageToSW(ServiceWorkerMessageType.UPDATE_MANIFEST, {
            manifest: this.manifest,
          });
        } catch (swError) {
          // Log but don't fail - SW sync is best-effort
          debugLog("Failed to sync manifest with service worker:", swError);
        }
      }

      debugLog("Cache manifest saved");
    } catch (error) {
      if (error instanceof CacheError) {
        this.lastError = error;
        throw error;
      }

      const cacheError = new ManifestSaveFailedError(
        error instanceof Error ? error : undefined
      );
      this.lastError = cacheError;
      debugLog("Failed to save cache manifest:", cacheError);
      throw cacheError;
    }
  }

  /**
   * Add an asset to the manifest
   * @param url URL of the asset
   * @param metadata Asset metadata
   */
  static async addToManifest(
    url: string,
    metadata: Partial<AssetMetadata>
  ): Promise<void> {
    try {
      await this.ensureInitialized();

      if (!this.manifest) {
        await this.loadManifest();
      }

      if (!this.manifest) {
        throw new CacheNotInitializedError("addToManifest");
      }

      // Create or update the asset entry
      const cacheKey = generateCacheKey(url);

      this.manifest.assets[cacheKey] = {
        url,
        type: metadata.type || determineAssetType(url),
        version: metadata.version || Date.now().toString(36),
        lastModified: metadata.lastModified || new Date().toISOString(),
        size: metadata.size,
        tags: metadata.tags,
      };

      // Save the manifest
      await this.saveManifest();
    } catch (error) {
      const cacheError = wrapError(error, "Failed to add asset to manifest");
      this.lastError = cacheError;
      debugLog("Failed to add asset to manifest:", cacheError);
      // Don't throw - this is a non-critical operation
    }
  }

  /**
   * Remove an asset from the manifest
   * @param url URL of the asset
   */
  static async removeFromManifest(url: string): Promise<void> {
    try {
      await this.ensureInitialized();

      if (!this.manifest) {
        await this.loadManifest();
      }

      if (!this.manifest) {
        throw new CacheNotInitializedError("removeFromManifest");
      }

      // Remove the asset entry
      const cacheKey = generateCacheKey(url);

      if (this.manifest.assets[cacheKey]) {
        delete this.manifest.assets[cacheKey];

        // Save the manifest
        await this.saveManifest();
      }
    } catch (error) {
      const cacheError = wrapError(error, "Failed to remove asset from manifest");
      this.lastError = cacheError;
      debugLog("Failed to remove asset from manifest:", cacheError);
      // Don't throw - this is a non-critical operation
    }
  }

  /**
   * Get asset metadata from the manifest
   * @param url URL of the asset
   * @returns Asset metadata or null if not found
   */
  static async getAssetMetadata(url: string): Promise<AssetMetadata | null> {
    try {
      await this.ensureInitialized();

      if (!this.manifest) {
        await this.loadManifest();
      }

      if (!this.manifest) {
        return null;
      }

      const cacheKey = generateCacheKey(url);
      return this.manifest.assets[cacheKey] || null;
    } catch (error) {
      debugLog("Failed to get asset metadata:", error);
      return null;
    }
  }

  /**
   * Get all assets of a specific type
   * @param type Asset type
   * @returns Array of asset metadata
   */
  static async getAssetsByType(type: AssetType): Promise<AssetMetadata[]> {
    try {
      await this.ensureInitialized();

      if (!this.manifest) {
        await this.loadManifest();
      }

      if (!this.manifest) {
        return [];
      }

      return Object.values(this.manifest.assets).filter(
        (asset) => asset.type === type
      );
    } catch (error) {
      debugLog("Failed to get assets by type:", error);
      return [];
    }
  }

  /**
   * Get all assets with a specific tag
   * @param tag Tag to filter by
   * @returns Array of asset metadata
   */
  static async getAssetsByTag(tag: string): Promise<AssetMetadata[]> {
    try {
      await this.ensureInitialized();

      if (!this.manifest) {
        await this.loadManifest();
      }

      if (!this.manifest) {
        return [];
      }

      return Object.values(this.manifest.assets).filter((asset) =>
        asset.tags?.includes(tag)
      );
    } catch (error) {
      debugLog("Failed to get assets by tag:", error);
      return [];
    }
  }

  /**
   * Get cache statistics
   * @returns Cache statistics
   */
  static async getStats(): Promise<{
    totalAssets: number;
    totalSize: number;
    assetsByType: Record<AssetType, number>;
    formattedSize: string;
    state: CacheManagerState;
    lastError: CacheError | null;
  }> {
    try {
      await this.ensureInitialized();

      if (!this.manifest) {
        await this.loadManifest();
      }

      if (!this.manifest) {
        return this.getEmptyStats();
      }

      const assets = Object.values(this.manifest.assets);
      const totalAssets = assets.length;
      const totalSize = assets.reduce(
        (sum, asset) => sum + (asset.size || 0),
        0
      );

      // Count assets by type
      const assetsByType = assets.reduce(
        (counts, asset) => {
          counts[asset.type] = (counts[asset.type] || 0) + 1;
          return counts;
        },
        {} as Record<AssetType, number>
      );

      return {
        totalAssets,
        totalSize,
        assetsByType,
        formattedSize: formatSize(totalSize),
        state: this.state,
        lastError: this.lastError,
      };
    } catch (error) {
      debugLog("Failed to get cache stats:", error);
      return this.getEmptyStats();
    }
  }

  /**
   * Get empty stats object
   */
  private static getEmptyStats(): {
    totalAssets: number;
    totalSize: number;
    assetsByType: Record<AssetType, number>;
    formattedSize: string;
    state: CacheManagerState;
    lastError: CacheError | null;
  } {
    return {
      totalAssets: 0,
      totalSize: 0,
      assetsByType: {} as Record<AssetType, number>,
      formattedSize: "0 B",
      state: this.state,
      lastError: this.lastError,
    };
  }

  /**
   * Clear the cache and manifest
   */
  static async clear(): Promise<void> {
    try {
      if (!this.isSupported()) {
        return;
      }

      // Delete the cache
      await caches.delete(DEFAULT_CACHE_NAME);

      // Reset the manifest
      this.manifest = this.createNewManifest();

      // Reset the cache reference
      this.cache = null;

      // Reset state (but keep as READY since clear was successful)
      if (this.state === CacheManagerState.READY) {
        // Will need to re-init on next operation
        this.state = CacheManagerState.UNINITIALIZED;
      }

      debugLog("Cache and manifest cleared");
    } catch (error) {
      const cacheError = wrapError(error, "Failed to clear cache and manifest");
      this.lastError = cacheError;
      debugLog("Failed to clear cache and manifest:", cacheError);
      // Don't throw - clearing is best-effort
    }
  }

  /**
   * Purge expired assets from the cache
   * @returns Number of assets purged
   */
  static async purgeExpired(): Promise<number> {
    try {
      await this.ensureInitialized();

      if (!this.cache || !this.manifest) {
        return 0;
      }

      const now = Date.now();
      const expiredAssets: string[] = [];

      // Find expired assets
      for (const [cacheKey, metadata] of Object.entries(this.manifest.assets)) {
        // Check if the asset has an expiration time
        if (metadata.lastModified) {
          const lastModifiedTime = new Date(metadata.lastModified).getTime();
          const age = now - lastModifiedTime;

          // If older than the default TTL, consider it expired
          if (age > DEFAULT_CACHE_TTL * 1000) {
            expiredAssets.push(cacheKey);
          }
        }
      }

      // Remove expired assets
      for (const cacheKey of expiredAssets) {
        try {
          await this.cache.delete(cacheKey);
          delete this.manifest.assets[cacheKey];
        } catch (deleteError) {
          debugLog(`Failed to delete expired asset ${cacheKey}:`, deleteError);
          // Continue with other assets
        }
      }

      // Save the manifest if any assets were removed
      if (expiredAssets.length > 0) {
        await this.saveManifest();
        debugLog(`Purged ${expiredAssets.length} expired assets`);
      }

      return expiredAssets.length;
    } catch (error) {
      const cacheError = wrapError(error, "Failed to purge expired assets");
      this.lastError = cacheError;
      debugLog("Failed to purge expired assets:", cacheError);
      return 0;
    }
  }
}
